// Mosaic renderer — ports the HTML prototype's `renderMosaic`
// (lines 1046–1272). Cells are sampled from the image, optionally
// dithered with Floyd-Steinberg, and rendered as colored dots. Edge
// roughness applies dropout + jitter near outer or content edges.
//
// Differences from the HTML source:
// - Image guard goes through `s.image.sampled` (typed) rather than the
//   prototype's `s.image.brightness` array.
// - `cellColors` is a fixed-length tuple array (`Array<RgbTriplet | undefined>`)
//   sized to match the cell grid.

import { hexToRgb } from '../core/color';
import { averageAlpha, averageBrightness, averageColor } from '../core/image-sample';
import { makeRng } from '../core/rng';
import type { PatternState, Render2DContext, RenderFn, RgbTriplet } from '../types';

interface QuantResult {
  idx: number;
  value: number;
}

export const renderMosaic: RenderFn = (
  ctx: Render2DContext,
  W: number,
  H: number,
  s: PatternState,
) => {
  const colors = s.palette.map((hex) => hexToRgb(hex));

  const mp = s.mosaicParams;
  const p = {
    cellSize: mp.cellSize ?? 16,
    mBrightness: mp.mBrightness ?? 0,
    mContrast: mp.mContrast ?? 1.3,
    mInvert: mp.mInvert ?? 0,
    mThreshold: mp.mThreshold ?? 0.5,
    dotScale: mp.dotScale ?? 0.75,
  };

  ctx.clearRect(0, 0, W, H);
  if (s.backgroundMode !== 'transparent') {
    ctx.fillStyle = s.bg;
    ctx.fillRect(0, 0, W, H);
  }

  if (!s.image.loaded || s.image.sampled === null) {
    ctx.fillStyle = 'rgba(255,255,255,0.3)';
    ctx.font = `${Math.min(W, H) * 0.03}px Inter, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Upload an image to start', W / 2, H / 2);
    return;
  }

  const sampled = s.image.sampled;
  const canvasAspect = W / H;
  const imgAspect = sampled.sampleW / sampled.sampleH;
  // Crop anchor for the "cover" mapping. Defaults to 0.5 (centred) so
  // existing consumers see no behaviour change. Outcome bar images set
  // `cropAnchorY = 0` to keep heads inside the rasterised window when the
  // source is portrait and the canvas is wide.
  const cropAnchorX = mp.cropAnchorX ?? 0.5;
  const cropAnchorY = mp.cropAnchorY ?? 0.5;
  let uScale = 1;
  let vScale = 1;
  let uOffset = 0;
  let vOffset = 0;
  if (imgAspect > canvasAspect) {
    uScale = canvasAspect / imgAspect;
    uOffset = (1 - uScale) * cropAnchorX;
  } else if (imgAspect < canvasAspect) {
    vScale = imgAspect / canvasAspect;
    vOffset = (1 - vScale) * cropAnchorY;
  }

  // `cellSize` from params is reinterpreted as the TARGET CELLS-ACROSS the
  // shorter dimension, not the pixel size of one cell. The renderer derives
  // pixel cell size from output dimensions so a face in a 1200×675 hero
  // gets ~80 cells of detail, and the same face in a 24×24 avatar gets
  // 80 cells clamped to 2-px each — the dot pattern is then resolution-
  // independent and reads well at every output size.
  //
  // Clamp pixel cell size to [2, 64]: 2-px floor lets small avatars retain
  // facial structure, 64-px ceiling prevents single-cell oversized dots on
  // very large heroes.
  const targetCellsAcross = p.cellSize;
  const shorterDim = Math.min(W, H);
  const cellPx = Math.max(2, Math.min(64, Math.round(shorterDim / targetCellsAcross)));
  const cols = Math.max(2, Math.floor(W / cellPx));
  const rows = Math.max(2, Math.floor(H / cellPx));

  const isOriginalColorMode = s.mosaic.colorMode === 'original';
  const bright = new Float32Array(cols * rows);
  const coverage = new Float32Array(cols * rows);
  const cellColors: Array<RgbTriplet | undefined> = isOriginalColorMode
    ? new Array<RgbTriplet | undefined>(cols * rows)
    : [];

  for (let cy = 0; cy < rows; cy++) {
    const v0Canvas = cy / rows;
    const v1Canvas = (cy + 1) / rows;
    const iv0 = vOffset + v0Canvas * vScale;
    const iv1 = vOffset + v1Canvas * vScale;
    for (let cx = 0; cx < cols; cx++) {
      const u0Canvas = cx / cols;
      const u1Canvas = (cx + 1) / cols;
      const iu0 = uOffset + u0Canvas * uScale;
      const iu1 = uOffset + u1Canvas * uScale;

      let b = averageBrightness(sampled, iu0, iu1, iv0, iv1);
      b += p.mBrightness;
      b = (b - 0.5) * p.mContrast + 0.5;
      if (p.mInvert) b = 1 - b;
      const shift = 0.5 - p.mThreshold;
      b = Math.max(0, Math.min(1, b + shift));
      bright[cy * cols + cx] = b;
      coverage[cy * cols + cx] = averageAlpha(sampled, iu0, iu1, iv0, iv1);

      if (isOriginalColorMode) {
        cellColors[cy * cols + cx] = averageColor(sampled, iu0, iu1, iv0, iv1);
      }
    }
  }

  const levelCount = colors.length;
  const quant = (v: number): QuantResult => {
    const idx = Math.round(v * (levelCount - 1));
    return { idx, value: idx / (levelCount - 1) };
  };

  const paletteIdx = new Int16Array(cols * rows);

  if (s.mosaic.dither) {
    const work = new Float32Array(bright);
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const idx = cy * cols + cx;
        const oldV = work[idx] ?? 0;
        const q = quant(oldV);
        paletteIdx[idx] = q.idx;
        const err = oldV - q.value;
        if (cx + 1 < cols) work[idx + 1] = (work[idx + 1] ?? 0) + (err * 7) / 16;
        if (cy + 1 < rows && cx > 0)
          work[idx + cols - 1] = (work[idx + cols - 1] ?? 0) + (err * 3) / 16;
        if (cy + 1 < rows) work[idx + cols] = (work[idx + cols] ?? 0) + (err * 5) / 16;
        if (cy + 1 < rows && cx + 1 < cols)
          work[idx + cols + 1] = (work[idx + cols + 1] ?? 0) + (err * 1) / 16;
      }
    }
  } else {
    for (let i = 0; i < cols * rows; i++) {
      paletteIdx[i] = quant(bright[i] ?? 0).idx;
    }
  }

  // ========== EDGE ROUGHNESS ==========
  const edgeMode = s.mosaic.edgeMode;
  const edgeDepth = s.mosaic.edgeDepth || 5;
  const edgeRoughness = s.mosaic.edgeRoughness || 0.5;
  const edgeActive = edgeMode !== 'off' && edgeRoughness > 0;

  let contentEdge: Float32Array | null = null;
  if (edgeActive && (edgeMode === 'content' || edgeMode === 'both')) {
    contentEdge = new Float32Array(cols * rows);
    for (let cy = 0; cy < rows; cy++) {
      for (let cx = 0; cx < cols; cx++) {
        const idx = cy * cols + cx;
        const b = bright[idx] ?? 0;
        const br = cx < cols - 1 ? (bright[idx + 1] ?? b) : b;
        const bb = cy < rows - 1 ? (bright[idx + cols] ?? b) : b;
        const grad = Math.abs(b - br) + Math.abs(b - bb);
        contentEdge[idx] = Math.min(1, grad * 4);
      }
    }
  }

  function edgeProximity(cx: number, cy: number): number {
    if (!edgeActive) return 0;
    let proximity = 0;
    if (edgeMode === 'outer' || edgeMode === 'both') {
      const distL = cx;
      const distR = cols - 1 - cx;
      const distT = cy;
      const distB = rows - 1 - cy;
      const minDist = Math.min(distL, distR, distT, distB);
      if (minDist < edgeDepth) {
        proximity = Math.max(proximity, 1 - minDist / edgeDepth);
      }
    }
    if ((edgeMode === 'content' || edgeMode === 'both') && contentEdge) {
      const r = Math.min(3, Math.floor(edgeDepth / 2));
      let maxGrad = 0;
      for (let dy = -r; dy <= r; dy++) {
        for (let dx = -r; dx <= r; dx++) {
          const nx = cx + dx;
          const ny = cy + dy;
          if (nx < 0 || nx >= cols || ny < 0 || ny >= rows) continue;
          const dist = Math.max(Math.abs(dx), Math.abs(dy));
          const falloff = 1 - dist / (r + 1);
          maxGrad = Math.max(maxGrad, (contentEdge[ny * cols + nx] ?? 0) * falloff);
        }
      }
      proximity = Math.max(proximity, maxGrad);
    }
    return proximity;
  }

  // ========== DRAW CELLS ==========
  const cellW = W / cols;
  const cellH = H / rows;

  const dotRadius = Math.min(cellW, cellH) * 0.5 * p.dotScale;
  const edgeRng = makeRng(s.seed + 7777);

  const fallback: RgbTriplet = [0, 0, 0];

  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const pIdx = paletteIdx[cy * cols + cx] ?? 0;
      if (pIdx === 0 || (coverage[cy * cols + cx] ?? 0) <= 0) continue;

      const prox = edgeProximity(cx, cy);
      if (prox > 0 && edgeActive) {
        const dropChance = prox * edgeRoughness * 0.7;
        if (edgeRng() < dropChance) continue;
      }

      const col: RgbTriplet = isOriginalColorMode
        ? (cellColors[cy * cols + cx] ?? fallback)
        : (colors[pIdx] ?? fallback);
      let x = cx * cellW;
      let y = cy * cellH;

      if (prox > 0 && edgeActive) {
        const maxJitter = Math.min(cellW, cellH) * 0.4 * prox * edgeRoughness;
        x += (edgeRng() - 0.5) * 2 * maxJitter;
        y += (edgeRng() - 0.5) * 2 * maxJitter;
      }

      ctx.fillStyle = `rgb(${col[0] | 0},${col[1] | 0},${col[2] | 0})`;
      ctx.beginPath();
      ctx.arc(x + cellW / 2, y + cellH / 2, dotRadius, 0, Math.PI * 2);
      const previousAlpha = ctx.globalAlpha;
      ctx.globalAlpha = coverage[cy * cols + cx] ?? 1;
      ctx.fill();
      ctx.globalAlpha = previousAlpha;
    }
  }
};
